import {lshGETAUTH} from './core';
import {graphqlRequestHandler} from './core';
import {IFarmFieldLogData, IGenericType} from 'constants/interfaces';

export interface IPestDiseaseOption {
  id: string;
  name: string;
}

export async function apiCreateFarmNote(
  input: {
    comment: string;
    noteTypeId: number | string;
    farmId: number | string;
    farmFieldId?: number | string | null;
    lat?: number | null;
    long?: number | null;
  },
  farmFieldId: number | string | null,
  farmId: number | string,
): Promise<any> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};

    const variables = {
      input,
      farmFieldId: farmFieldId ?? undefined,
      farmId,
    };

    const mutation = `
      mutation CreateFarmNote($input: FarmNoteInput!, $farmId: ID!, $farmFieldId: ID) {
        createFarmNote(input: $input, farmId: $farmId, farmFieldId: $farmFieldId) {
          id
          createdAt
        }
      }
    `;

    return new Promise((resolve, reject) => {
      graphqlRequestHandler('createFarmNote', mutation, variables, headers)
        .then((data) => resolve(data))
        .catch(reject);
    });
  });
}

export async function apiCreateSoilAssessment(input: {
  farmId: number | string;
  farmFieldId?: number | string | null;
  noteId?: number | string | null;
  soilType?: string;
  soilStructure?: string;
  hardpanPresence?: boolean;
  organicMatterPct?: number;
  pH?: number;
  P?: number;
  K?: number;
  Ca?: number;
  Na?: number;
  Mg?: number;
  Zn?: number;
  Mn?: number;
  Cu?: number;
  anions?: number;
  exchangeableAcidity?: number;
  totalCations?: number;
  soilAmendmentsNeeded?: string;
  suggestedCropVarieties?: string;
  pestWeedStrategies?: string;
}): Promise<any> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {input};
    const mutation = `
      mutation CreateSoilAssessment($input: SoilAssessmentInput!) {
        createSoilAssessment(input: $input) {
          id
          createdAt
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'createSoilAssessment',
        mutation,
        variables,
        headers,
      )
        .then((data) => resolve(data))
        .catch(reject);
    });
  });
}

export async function apiUpdateSoilAssessment(
  id: number | string,
  input: {
    farmFieldId?: number | string | null;
    soilStructure?: string;
    hardpanPresence?: boolean;
    organicMatterPct?: number;
    pH?: number;
    P?: number;
    K?: number;
    Ca?: number;
    Na?: number;
    Mg?: number;
    Zn?: number;
    Mn?: number;
    Cu?: number;
    anions?: number;
    exchangeableAcidity?: number;
    totalCations?: number;
    soilAmendmentsNeeded?: string;
    suggestedCropVarieties?: string;
    pestWeedStrategies?: string;
  },
): Promise<any> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {id, input};
    const mutation = `
      mutation UpdateSoilAssessment($id: ID!, $input: SoilAssessmentInput!) {
        updateSoilAssessment(soilAssessmentId: $id, input: $input) {
          id
          updatedAt
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'updateSoilAssessment',
        mutation,
        variables,
        headers,
      )
        .then((data) => resolve(data))
        .catch(reject);
    });
  });
}

const SOIL_ASSESSMENT_SELECTION = `
  id
  noteId
  soilType
  soilStructure
  hardpanPresence
  organicMatterPct
  pH
  P
  K
  Ca
  Mg
  Na
  Zn
  Mn
  Cu
  exchangeableAcidity
  totalCations
  anions
  soilAmendmentsNeeded
  suggestedCropVarieties
  pestWeedStrategies
  createdAt
`;

export async function apiGetSoilAssessments(
  farmFieldId: number | string | null,
): Promise<any[]> {
  if (farmFieldId === null || farmFieldId === undefined || farmFieldId === '') {
    return [];
  }

  try {
    return await lshGETAUTH().then((token) => {
      const headers = {Authorization: token};
      const variables = {farmFieldId};
      const query = `
        query GetSoilAssessments($farmFieldId: ID!) {
          soilAssessments(farmFieldId: $farmFieldId) {
            ${SOIL_ASSESSMENT_SELECTION}
          }
        }
      `;
      return new Promise<any[]>((resolve, reject) => {
        graphqlRequestHandler('soilAssessments', query, variables, headers)
          .then((data) => resolve(Array.isArray(data) ? data : []))
          .catch(reject);
      });
    });
  } catch (error) {
    try {
      const latest = await apiGetLatestSoilAssessment(farmFieldId);
      return latest ? [latest] : [];
    } catch (fallbackError) {
      console.error(
        'Failed to fetch soil assessments (list + latest fallback)',
        error,
        fallbackError,
      );
      return [];
    }
  }
}

export async function apiGetLatestSoilAssessment(
  farmFieldId: number | string | null,
): Promise<any> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {farmFieldId};
    const query = `
      query GetLatestSoilAssessment($farmFieldId: ID!) {
        latestSoilAssessment(farmFieldId: $farmFieldId) {
          id
          soilType
          soilStructure
          hardpanPresence
          organicMatterPct
          pH
          P
          K
          Ca
          Mg
          Na
          Zn
          Mn
          Cu
          exchangeableAcidity
          totalCations
          anions
          soilAmendmentsNeeded
          suggestedCropVarieties
          pestWeedStrategies
          createdAt
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler('latestSoilAssessment', query, variables, headers)
        .then((data) => resolve(data))
        .catch(reject);
    });
  });
}

export async function apiCreateIssueLog(input: {
  farmId: number | string;
  farmFieldId?: number | string | null;
  noteId?: number | string | null;
  pestDiseaseId?: number | string | null;
  pestsAndDiseasesId?: number | string | null;
  issueDate: string;
  issueType: string;
  severityScale: number;
  controlMeasuresApplied: string;
  preventativeMeasuresTaken?: string;
  issueDuringHarvest?: boolean;
}): Promise<any> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {input};
    const mutation = `
      mutation CreateIssueLog($input: IssueLogInput!) {
        createIssueLog(input: $input) {
          id
          createdAt
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler('createIssueLog', mutation, variables, headers)
        .then((data) => resolve(data))
        .catch(reject);
    });
  });
}

export async function apiUpdateIssueLog(
  id: number | string,
  input: {
    pestDiseaseId?: number | string | null;
    pestsAndDiseasesId?: number | string | null;
    issueDate?: string;
    issueType?: string;
    severityScale?: number;
    controlMeasuresApplied?: string;
    preventativeMeasuresTaken?: string;
    issueDuringHarvest?: boolean;
  },
): Promise<any> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {id, input};
    const mutation = `
      mutation UpdateIssueLog($id: ID!, $input: IssueLogInput!) {
        updateIssueLog(issueLogId: $id, input: $input) {
          id
          updatedAt
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler('updateIssueLog', mutation, variables, headers)
        .then((data) => resolve(data))
        .catch(reject);
    });
  });
}

export async function apiGetLatestIssueLog(
  farmFieldId: number | string | null,
): Promise<any> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {farmFieldId};
    const query = `
      query GetLatestIssueLog($farmFieldId: ID!) {
        latestIssueLog(farmFieldId: $farmFieldId) {
          id
          issueDate
          issueType
          severityScale
          controlMeasuresApplied
          preventativeMeasuresTaken
          issueDuringHarvest
          createdAt
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler('latestIssueLog', query, variables, headers)
        .then((data) => resolve(data))
        .catch(reject);
    });
  });
}

export async function apiGetFarmNotes(
  farmFieldId: number | string | null,
): Promise<any[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {farmFieldId};
    const query = `
      query GetFarmNotes($farmFieldId: ID!) {
        farmNotes(farmFieldId: $farmFieldId) {
          id
          comment
          lat
          long
          imageUrl
          createdAt
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler('farmNotes', query, variables, headers)
        .then((data) => resolve(data))
        .catch(reject);
    });
  });
}

const FARM_FIELD_LOGS_SELECTION = `
  id
  info {
    hectares
    fieldName
    variety
    plantedOn
    plantPopulation
    irrigation
    expectedYield
    produce {
      id
      name
    }
  }
  notes {
    id
    noteDate
    noteText
    title
    comment
    lat
    long
    isCritical
    createdAt
    updatedAt
    images {
      id
      imageUrl
      caption
    }
  }
  growthObservations {
    id
    noteId
    observationDate
    cropGrowthStage
    averagePlantHeightCm
    leafAreaIndex
    overallCropHealthRating
    pestAndDiseasePresence
    weedPressure
    floweringIntensity
    fruitSetIntensity
    estimatedYieldKgha
    germinationDate
    expectedGerminationRate
    estimatedYield
    yieldEstimate
    plantHeightCm
    cropMaturityAssessment
    germinationRatePct
    seedlingVigorScale
    rootLengthScale
    uniformityOfMaturityScale
    leavesPerPlant
    floweringUniformityScale
    fruitSetPercent
    fruitSetUniformityScale
    fruitAvgLengthCm
    pctCropReadyForHarvest
    createdAt
    updatedAt
  }
  issueLogs {
    id
    noteId
    issueDate
    issueType
    description
    severityScale
    affectedAreaHectares
    controlMeasuresApplied
    preventativeMeasuresTaken
    curativeMeasuresTaken
    issueStatus
    issueDuringHarvest
    createdAt
    updatedAt
  }
  managementLogs {
    id
    noteId
    logDate
    activityType
    inputsUsed
    fertilizerType
    applicationRateKgha
    laborHours
    machineryUsed
    irrigationFrequency
    irrigationVolume
    weatherConditions
    waterStressSigns
    initialTreatmentActions
    createdAt
    updatedAt
  }
  harvestSummaries {
    id
    noteId
    harvestDate
    totalHarvestedQuantityKg
    avgYieldPerHectareKgha
    harvestMethod
    pctFirstGradeProduce
    commonQualityIssues
    harvestEfficiencyScale
    qualityGradesDistribution
    laborersUsed
    equipmentUsed
    initialProcessingSteps
    storageConditions
    transportationArrangements
    createdAt
    updatedAt
  }
  postHarvestReviews {
    id
    noteId
    reviewDate
    marketPricesAchieved
    costOfProductionAnalysis
    profitabilityAssessment
    laborEfficiencyChallenges
    equipmentPerformanceIssues
    recommendationsForNextSeason
    areasForImprovement
    keySuccesses
    comparisonWithExpectedYields
    pestDiseaseEffectiveness
    nutrientMgmtEffectiveness
    adjustmentsMadeDuringSeason
    impactOnYieldQuality
    lessonsLearned
    createdAt
    updatedAt
  }
`;

export async function apiGetFarmFieldWithLogs(
  fieldId: number | string,
): Promise<IFarmFieldLogData | null> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {fieldId};
    const query = `
      query GetFarmFieldWithLogs($fieldId: ID!) {
        getFarmField(fieldId: $fieldId) {
          ${FARM_FIELD_LOGS_SELECTION}
        }
      }
    `;

    return new Promise((resolve, reject) => {
      graphqlRequestHandler('getFarmField', query, variables, headers)
        .then((data) => resolve((data as IFarmFieldLogData) ?? null))
        .catch(reject);
    });
  });
}

export async function apiGetFarmFieldsWithLogs(
  farmId: number | string,
): Promise<IFarmFieldLogData[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {farmId};
    const query = `
      query GetFarmFieldsWithLogs($farmId: ID!) {
        getFarmFields(farmId: $farmId) {
          ${FARM_FIELD_LOGS_SELECTION}
        }
      }
    `;

    return new Promise((resolve, reject) => {
      graphqlRequestHandler('getFarmFields', query, variables, headers)
        .then((data) => resolve((data as IFarmFieldLogData[]) ?? []))
        .catch(reject);
    });
  });
}

export async function apiCreateGrowthObservation(input: any): Promise<any> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {input};
    const mutation = `
      mutation CreateGrowthObservation($input: GrowthObservationInput!) {
        createGrowthObservation(input: $input) {
          id
          createdAt
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'createGrowthObservation',
        mutation,
        variables,
        headers,
      )
        .then((data) => resolve(data))
        .catch(reject);
    });
  });
}

export async function apiUpdateGrowthObservation(
  id: string | number,
  input: any,
): Promise<any> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {id, input};
    const mutation = `
      mutation UpdateGrowthObservation($id: ID!, $input: GrowthObservationInput!) {
        updateGrowthObservation(growthObservationId: $id, input: $input) {
          id
          updatedAt
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'updateGrowthObservation',
        mutation,
        variables,
        headers,
      )
        .then((data) => resolve(data))
        .catch(reject);
    });
  });
}

export async function apiGetLatestGrowthObservation(
  farmFieldId: number | string | null,
): Promise<any> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {farmFieldId};
    const query = `
      query GetLatestGrowthObservation($farmFieldId: ID!) {
        latestGrowthObservation(farmFieldId: $farmFieldId) {
          id
          stage
          observationDate
          latestAcceptablePlantingDate
          expectedGerminationRate
          estimatedYield
          plannedHa
          haPrepared
          rowSpacing
          plantedPct
          plantHeightCm
          germinationRatePct
          seedlingVigorScale
          rootLengthScale
          cropMaturityAssessment
          uniformityOfMaturity
          leavesPerPlant
          floweringUniformityScale
          marketReady
          marketReadyComment
          tonsHedged
          fruitSetPercent
          fruitSetUniformityScale
          fruitAvgLengthCm
          fruitAvgDiameterCm
          pctCropReadyForHarvest
          estimatedHarvestDate
          logisticsReady
          comment
          createdAt
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'latestGrowthObservation',
        query,
        variables,
        headers,
      )
        .then((data) => resolve(data))
        .catch(reject);
    });
  });
}

export async function apiCreateManagementLog(input: {
  farmId: number | string;
  farmFieldId?: number | string | null;
  noteId?: number | string | null;
  logDate: string;
  activityType: string;
  fertilizerType?: string;
  applicationRateKgha?: number;
  irrigationFrequency?: string;
  irrigationVolume?: number;
  waterStressSigns?: string;
  initialTreatmentActions?: string;
}): Promise<any> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {input};
    const mutation = `
      mutation CreateManagementLog($input: ManagementLogInput!) {
        createManagementLog(input: $input) {
          id
          createdAt
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler('createManagementLog', mutation, variables, headers)
        .then((data) => resolve(data))
        .catch(reject);
    });
  });
}

export async function apiGetLatestManagementLog(
  farmFieldId: number | string,
): Promise<any> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {farmFieldId};
    const query = `
      query GetLatestManagementLog($farmFieldId: ID!) {
        latestManagementLog(farmFieldId: $farmFieldId) {
          id
          logDate
          activityType
          fertilizerType
          applicationRateKgha
          irrigationFrequency
          irrigationVolume
          waterStressSigns
          initialTreatmentActions
          comment
          createdAt
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler('latestManagementLog', query, variables, headers)
        .then((data) => resolve(data))
        .catch(reject);
    });
  });
}

export async function apiCreateHarvestSummary(input: {
  farmId: number | string;
  farmFieldId?: number | string | null;
  noteId?: number | string | null;
  totalHarvestedQuantityKg?: number;
  avgYieldPerHectareKgha?: number;
  harvestMethod?: string;
  pctFirstGradeProduce?: number;
  commonQualityIssues?: string;
  harvestEfficiencyScale?: number;
  laborersUsed?: number;
  equipmentUsed?: string;
  initialProcessingSteps?: string;
  storageConditions?: string;
  transportationArrangements?: string;
}): Promise<any> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {input};
    const mutation = `
      mutation CreateHarvestSummary($input: HarvestSummaryInput!) {
        createHarvestSummary(input: $input) {
          id
          createdAt
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'createHarvestSummary',
        mutation,
        variables,
        headers,
      )
        .then((data) => resolve(data))
        .catch(reject);
    });
  });
}

export async function apiGetLatestHarvestSummary(
  farmFieldId: number | string,
): Promise<any> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {farmFieldId};
    const query = `
      query GetLatestHarvestSummary($farmFieldId: ID!) {
        latestHarvestSummary(farmFieldId: $farmFieldId) {
          id
          totalHarvestedQuantityKg
          avgYieldPerHectareKgha
          harvestMethod
          pctFirstGradeProduce
          commonQualityIssues
          harvestEfficiencyScale
          laborersUsed
          equipmentUsed
          initialProcessingSteps
          storageConditions
          transportationArrangements
          comment
          createdAt
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler('latestHarvestSummary', query, variables, headers)
        .then((data) => resolve(data))
        .catch(reject);
    });
  });
}

export async function apiCreatePostHarvestReview(input: {
  farmId: number | string;
  farmFieldId?: number | string | null;
  noteId?: number | string | null;
  comparisonWithExpectedYields?: string;
  pestDiseaseEffectiveness?: string;
  nutrientMgmtEffectiveness?: string;
  adjustmentsMadeDuringSeason?: string;
  impactOnYieldQuality?: string;
  marketPricesAchieved?: string;
  laborEfficiencyChallenges?: string;
  equipmentPerformanceIssues?: string;
  costOfProductionAnalysis?: string;
  profitabilityAssessment?: string;
  keySuccesses?: string;
  areasForImprovement?: string;
  recommendationsForNextSeason?: string;
  lessonsLearned?: string;
}): Promise<any> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {input};
    const mutation = `
      mutation CreatePostHarvestReview($input: PostHarvestReviewInput!) {
        createPostHarvestReview(input: $input) {
          id
          createdAt
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'createPostHarvestReview',
        mutation,
        variables,
        headers,
      )
        .then((data) => resolve(data))
        .catch(reject);
    });
  });
}

export async function apiGetLatestPostHarvestReview(
  farmFieldId: number | string,
): Promise<any> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {farmFieldId};
    const query = `
      query GetLatestPostHarvestReview($farmFieldId: ID!) {
        latestPostHarvestReview(farmFieldId: $farmFieldId) {
          id
          comparisonWithExpectedYields
          pestDiseaseEffectiveness
          nutrientMgmtEffectiveness
          adjustmentsMadeDuringSeason
          impactOnYieldQuality
          marketPricesAchieved
          laborEfficiencyChallenges
          equipmentPerformanceIssues
          costOfProductionAnalysis
          profitabilityAssessment
          keySuccesses
          areasForImprovement
          recommendationsForNextSeason
          lessonsLearned
          comment
          createdAt
        }
      }
    `;
    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'latestPostHarvestReview',
        query,
        variables,
        headers,
      )
        .then((data) => resolve(data))
        .catch(reject);
    });
  });
}

export async function apiGetPestDiseaseOptions(): Promise<
  IPestDiseaseOption[]
> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const attempts: Array<{operation: string; query: string}> = [
      {
        operation: 'getAllPestsAndDiseases',
        query: `
          query GetAllPestsAndDiseases {
            getAllPestsAndDiseases {
              id
              name
            }
          }
        `,
      },
      {
        operation: 'getAllPestDiseases',
        query: `
          query GetAllPestDiseases {
            getAllPestDiseases {
              id
              name
            }
          }
        `,
      },
      {
        operation: 'pestsAndDiseases',
        query: `
          query GetPestsAndDiseases {
            pestsAndDiseases {
              id
              name
            }
          }
        `,
      },
      {
        operation: 'pestDiseases',
        query: `
          query GetPestDiseases {
            pestDiseases {
              id
              name
            }
          }
        `,
      },
      {
        operation: 'getAllPests',
        query: `
          query GetAllPests {
            getAllPests {
              id
              name
            }
          }
        `,
      },
      {
        operation: 'getAllDiseases',
        query: `
          query GetAllDiseases {
            getAllDiseases {
              id
              name
            }
          }
        `,
      },
      {
        operation: 'allPestsAndDiseases',
        query: `
          query GetAllPestsAndDiseasesAlt {
            allPestsAndDiseases {
              id
              name
            }
          }
        `,
      },
    ];

    return new Promise(async (resolve, reject) => {
      const collectArraysDeep = (value: unknown): unknown[][] => {
        const arrays: unknown[][] = [];
        const visit = (node: unknown, depth: number) => {
          if (depth > 6 || node == null) return;
          if (Array.isArray(node)) {
            arrays.push(node);
            node.forEach((item) => visit(item, depth + 1));
            return;
          }
          if (typeof node === 'object') {
            Object.values(node as Record<string, unknown>).forEach((child) =>
              visit(child, depth + 1),
            );
          }
        };
        visit(value, 0);
        return arrays;
      };

      let lastErrorMessage = '';
      for (const attempt of attempts) {
        try {
          // graphqlRequestHandler extracts `data[name]` when `name` is truthy.
          // Some gateways may not match our guessed field name, so we ask for the
          // whole payload and then pick any array containing `id` + `name`.
          const payload = await graphqlRequestHandler(
            null,
            attempt.query,
            {},
            headers,
          );

          const candidateArrays = collectArraysDeep(payload);

          const options = candidateArrays
            .flat()
            .map((item: any) => {
              if (!item || typeof item !== 'object') return null;

              // Try direct fields
              const directId =
                item.id ??
                item.pestDiseaseId ??
                item.pestsAndDiseasesId ??
                item.pestAndDiseasesId ??
                item.pestAndDiseaseId ??
                item.diseaseId ??
                item.pestId;

              const directName =
                item.name ??
                item.pestDiseaseName ??
                item.pestsAndDiseasesName ??
                item.pestAndDiseasesName ??
                item.pestAndDiseaseName ??
                item.diseaseName ??
                item.pestName ??
                item.title;

              // Try common nesting patterns
              const nested =
                item.pestDisease?.id != null || item.pestDisease?.name
                  ? {id: item.pestDisease.id, name: item.pestDisease.name}
                  : item.pestsAndDiseases?.id != null ||
                    item.pestsAndDiseases?.name
                  ? {
                      id: item.pestsAndDiseases.id,
                      name: item.pestsAndDiseases.name,
                    }
                  : item.pestAndDisease?.id != null || item.pestAndDisease?.name
                  ? {id: item.pestAndDisease.id, name: item.pestAndDisease.name}
                  : item.pestDiseases?.id != null || item.pestDiseases?.name
                  ? {id: item.pestDiseases.id, name: item.pestDiseases.name}
                  : null;

              const id = nested?.id ?? directId;
              const name = (nested?.name ?? directName) as unknown;

              if (id == null) return null;

              return {
                id: String(id),
                name:
                  typeof name === 'string' && name.trim()
                    ? name.trim()
                    : `Pest/Disease ${String(id)}`,
              };
            })
            .filter(Boolean) as Array<{id: string; name: string}>;

          // De-dupe by id
          const seen = new Set<string>();
          const deduped = options.filter((o) => {
            if (seen.has(o.id)) return false;
            seen.add(o.id);
            return true;
          });

          if (deduped.length) {
            resolve(deduped);
            return;
          }
        } catch (error) {
          const raw = Array.isArray(error)
            ? error.join('; ')
            : String((error as any)?.message || error);
          lastErrorMessage = raw;
          // Try next query variant for schema compatibility.
        }
      }
      reject(
        new Error(lastErrorMessage || 'Failed to load pest/disease options'),
      );
    });
  });
}

const GET_IRRIGATION_TYPES = `
  query GetGeneralTypes($getIrrigationTypes: Boolean!) {
    getIrrigationTypes @include(if: $getIrrigationTypes) {
      id
      name
      type
      category
    }
  }
`;

export async function apiGetIrrigationTypes(): Promise<IGenericType[]> {
  return lshGETAUTH().then((data) => {
    const headers = {Authorization: data};
    const variables = {getIrrigationTypes: true};
    return new Promise((resolve, reject) => {
      graphqlRequestHandler(
        'getIrrigationTypes',
        GET_IRRIGATION_TYPES,
        variables,
        headers,
      )
        .then((res: IGenericType[]) => resolve(res || []))
        .catch((error) => reject(error));
    });
  });
}

export interface ISatelliteRisk {
  applicationId: number;
  farmerName: string;
  farmName: string;
  riskCount: number;
}

export async function apiGetSatelliteRisks(
  crmUserId: number,
): Promise<ISatelliteRisk[]> {
  return lshGETAUTH().then((token) => {
    const headers = {Authorization: token};
    const variables = {crmUserId};

    const query = `
      query getSatelliteRisks($crmUserId: Int!) {
        getSatelliteRisks(crmUserId: $crmUserId) {
          applicationId
          farmerName
          farmName
          riskCount
        }
      }
    `;

    return new Promise((resolve, reject) =>
      graphqlRequestHandler('getSatelliteRisks', query, variables, headers)
        .then((res) => resolve(res as ISatelliteRisk[]))
        .catch(reject),
    );
  });
}
